/**
 * Date Formatting Utilities
 * 
 * Consistent date formatting across the entire application.
 * Standard format: "Month Day, Year" (e.g., "March 10, 1989")
 */

/**
 * Format a date string or Date object to "Month Day, Year" format
 * 
 * @param {string|Date|null|undefined} dateInput - The date to format
 * @param {object} options - Formatting options
 * @param {boolean} options.includeTime - Include time in output (default: false)
 * @param {boolean} options.shortMonth - Use abbreviated month (default: false)
 * @param {string} options.fallback - Fallback text if date is invalid (default: "—")
 * @returns {string} Formatted date string
 * 
 * @example
 * formatDate("1987-06-29") // "June 29, 1987"
 * formatDate("2024-03-15T10:30:00Z", { includeTime: true }) // "March 15, 2024 at 10:30 AM"
 * formatDate(null) // "—"
 */
export const formatDate = (dateInput, options = {}) => {
  const { 
    includeTime = false, 
    shortMonth = false,
    fallback = "—" 
  } = options;

  if (!dateInput) return fallback;

  try {
    // Handle various date formats
    let date;
    
    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === 'string') {
      // Handle ISO dates, timestamps, and various formats
      date = new Date(dateInput);
    } else if (typeof dateInput === 'number') {
      date = new Date(dateInput);
    } else {
      return fallback;
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return fallback;
    }

    const formatOptions = {
      year: 'numeric',
      month: shortMonth ? 'short' : 'long',
      day: 'numeric',
    };

    if (includeTime) {
      formatOptions.hour = 'numeric';
      formatOptions.minute = '2-digit';
      formatOptions.hour12 = true;
    }

    return date.toLocaleDateString('en-US', formatOptions);
  } catch (error) {
    console.warn('Date formatting error:', error);
    return fallback;
  }
};

/**
 * Format a date for display in a compact format
 * "MMM D, YYYY" (e.g., "Mar 10, 1989")
 */
export const formatDateShort = (dateInput, fallback = "—") => {
  return formatDate(dateInput, { shortMonth: true, fallback });
};

/**
 * Format a date with time
 * "Month Day, Year at H:MM AM/PM"
 */
export const formatDateTime = (dateInput, fallback = "—") => {
  return formatDate(dateInput, { includeTime: true, fallback });
};

/**
 * Format a date range (e.g., for estimated dates)
 * 
 * @param {string|Date} startDate 
 * @param {string|Date} endDate 
 * @returns {string}
 */
export const formatDateRange = (startDate, endDate) => {
  const start = formatDate(startDate, { fallback: null });
  const end = formatDate(endDate, { fallback: null });

  if (start && end) {
    return `${start} - ${end}`;
  } else if (start) {
    return `After ${start}`;
  } else if (end) {
    return `Before ${end}`;
  }
  return "—";
};

/**
 * Format a year or year range
 * Handles cases like "1987", "1987-1990", or estimates
 */
export const formatYear = (year, fallback = "—") => {
  if (!year) return fallback;
  
  // If it's already just a year number
  if (typeof year === 'number' || /^\d{4}$/.test(year)) {
    return String(year);
  }
  
  // If it's a full date, extract just the year
  if (typeof year === 'string' && year.includes('-')) {
    try {
      const date = new Date(year);
      if (!isNaN(date.getTime())) {
        return String(date.getFullYear());
      }
    } catch {
      // Fall through
    }
  }
  
  return String(year);
};

/**
 * Get relative time string (e.g., "2 days ago", "in 3 hours")
 */
export const formatRelativeTime = (dateInput, fallback = "—") => {
  if (!dateInput) return fallback;

  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return fallback;

    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSecs < 60) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
    if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
    return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
  } catch {
    return fallback;
  }
};

export default formatDate;
